/**
 * Consumer hook for study feature.
 * Simple interface for UI components.
 */

import { useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useStudyPageDataQuery,
  useDiagnosisQuizSessionQuery,
  useWordPracticeSessionQuery,
  useWordQuizSessionQuery,
  useLevelPracticeSessionQuery,
  useTextExplanationSessionQuery,
  useRecommendationQuery,
  useSubjectLevelsQuery,
  useCreateSubjectQuizSessionMutation,
  useSubmitSubjectQuizAnswerMutation,
  useUpdateVocabularyProgressMutation,
  useCreateVocabularyQuizSessionMutation,
  useSubmitVocabularyQuizAnswerMutation,
  useDiagnosisQuizMetadataQuery,
  useSkipLevelAssessmentMutation,
  useCreateSubjectQuizPracticeSessionMutation,
  useSubmitSubjectQuizPracticeAnswerMutation,
  useBoostMessageQuery,
} from "@/infrastructure/adapters/study/study.query";
import {
  TStudySubject,
  TDiagnosisQuestion,
  TWordPracticeItem,
  TWordQuizQuestion,
  TSubjectLevelDTO,
  TLevelDiagnosis,
  TRecommendation,
} from "@/infrastructure/adapters/study/study.types";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useStudySettings } from "@/infrastructure/adapters/user-preference/user-preference.hook";

const DIAGNOSIS_EXPIRATION_MS = 10 * 60 * 1000; // 10 minutes

export const useStudy = () => {
  const query = useStudyPageDataQuery();
  const { settings } = useStudySettings();
  const isRecommendationEnabled = (settings?.isRecommendationEnabled ?? true) && (query.data?.subjects?.length ?? 0) > 0;
  const recQuery = useRecommendationQuery(isRecommendationEnabled);

  const subjects: TStudySubject[] = useMemo(() => query.data?.subjects ?? [], [query.data]);
  const recommendation: TRecommendation | null = isRecommendationEnabled ? (recQuery.data ?? null) : null;
  const isTrial: boolean = query.data?.isTrial ?? false;

  return {
    subjects,
    recommendation,
    isTrial,
    isLoading: query.isLoading || (isRecommendationEnabled && recQuery.isLoading),
    error: query.error,
  };
};

/** Consumer hook for word practice session */
export const useWordPractice = (subjectId: string) => {
  const queryClient = useQueryClient();
  const query = useWordPracticeSessionQuery(subjectId);
  const progressMutation = useUpdateVocabularyProgressMutation();

  const words: TWordPracticeItem[] = useMemo(() => query.data?.words ?? [], [query.data]);
  const totalCount = query.data?.totalCount ?? 0;

  /** Clear cached practice data and fetch fresh words from API (single request) */
  const resetAndRefetch = useCallback(() => {
    queryClient.resetQueries({ queryKey: [QUERY_KEYS.STUDY.WORD_PRACTICE] });
  }, [queryClient]);

  return {
    words,
    totalCount,
    isLoading: query.isLoading,
    error: query.error,
    resetAndRefetch,
    updateProgress: (vocabularyId: number) => progressMutation.mutateAsync({ subjectId, vocabularyId }),
  };
};

/** Consumer hook for word quiz session */
export const useWordQuiz = (subjectId: string) => {
  const query = useWordQuizSessionQuery(subjectId);

  const questions: TWordQuizQuestion[] = useMemo(() => query.data?.questions ?? [], [query.data]);
  const totalCount = query.data?.totalCount ?? 0;

  return {
    questions,
    totalCount,
    isLoading: query.isLoading,
    error: query.error,
  };
};

/** Consumer hook for diagnosis quiz session */
export const useDiagnosisQuiz = (subjectId: string, enabled = true) => {
  const query = useDiagnosisQuizSessionQuery(subjectId, enabled);

  const questions: TDiagnosisQuestion[] = useMemo(() => query.data?.questions ?? [], [query.data]);
  const totalCount = query.data?.totalCount ?? 0;
  const sessionId = query.data?.sessionId;
  const finishedAt = useMemo(() => new Date(Date.now() + DIAGNOSIS_EXPIRATION_MS).toISOString(), []);

  return {
    sessionId,
    questions,
    totalCount,
    finishedAt,
    isLoading: query.isLoading,
    error: query.error,
  };
};

export const useDiagnosisQuizMetadata = (subjectId: string, enabled = true) => {
  const query = useDiagnosisQuizMetadataQuery(subjectId, enabled);
  const totalQuestionCount = query.data?.totalQuestions ?? 0;
  const levelDiagnosis: TLevelDiagnosis | null =
    enabled && query.data
      ? {
          totalQuestionCount,
          estimatedMinutes: DIAGNOSIS_EXPIRATION_MS / 60_000, // Convert to 10 minutes
        }
      : null;
  const skipLevelAssessmentMutation = useSkipLevelAssessmentMutation();

  return {
    levelDiagnosis,
    isLoading: query.isLoading,
    error: query.error,
    skipLevelAssessment: () => skipLevelAssessmentMutation.mutateAsync({ subjectId }),
    isSkippingLevel: skipLevelAssessmentMutation.isPending,
  };
};

/** Consumer hook for level practice session (Lv1-Lv5 練習) */
export const useLevelPractice = (subjectId: string, level: number) => {
  const query = useLevelPracticeSessionQuery(subjectId, level);

  const questions: TDiagnosisQuestion[] = useMemo(() => query.data?.questions ?? [], [query.data]);
  const totalCount = query.data?.totalCount ?? 0;
  const levelName = query.data?.levelName ?? "";

  return {
    questions,
    totalCount,
    levelName,
    level: query.data?.level ?? level,
    isLoading: query.isLoading,
    error: query.error,
  };
};

/** Consumer hook for dashboard study data (returning users) */
export const useDashboardStudy = (args?: { enabled?: boolean }) => {
  const { settings } = useStudySettings();
  const isRecommendationEnabled = args?.enabled && (settings?.isRecommendationEnabled ?? true);
  const recQuery = useRecommendationQuery(isRecommendationEnabled);

  const recommendation: TRecommendation | null = isRecommendationEnabled ? (recQuery.data ?? null) : null;

  return {
    recommendation,
    isLoading: (isRecommendationEnabled && recQuery.isLoading) || false,
  };
};

/** Consumer hook for text explanation session (テキスト解説) */
export const useTextExplanation = (subjectId: string) => {
  const query = useTextExplanationSessionQuery(subjectId);

  const pages: string[] = useMemo(() => query.data?.pages ?? [], [query.data]);
  const subjectName = query.data?.subjectName ?? "";
  const totalPages = query.data?.totalPages ?? 0;

  return {
    pages,
    subjectName,
    totalPages,
    isLoading: query.isLoading,
  };
};

/** Consumer hook for subject levels + textbook URL (API 1) */
export const useSubjectLevels = (subjectId: string) => {
  const query = useSubjectLevelsQuery(subjectId);

  const levels: TSubjectLevelDTO[] = useMemo(() => (Array.isArray(query.data?.levels) ? query.data.levels : []), [query.data]);
  const textbookFileUrl = query.data?.textbookFileUrl ?? "";
  const vocabularyCount = query.data?.vocabularyCount ?? 0;
  const learnedVocabularyCount = query.data?.learnedVocabularyCount ?? 0;
  const isVocabularyQuizCompleted = query.data?.isVocabularyQuizCompleted ?? false;
  const currentLevel = query.data?.currentLevel ?? "lv0";

  return {
    levels,
    textbookFileUrl,
    vocabularyCount,
    learnedVocabularyCount,
    isVocabularyQuizCompleted,
    currentLevel,
    isLoading: query.isLoading,
    error: query.error,
  };
};

/** Consumer hook for subject quiz session management (API 2 + API 3) */
export const useSubjectQuiz = () => {
  const createSessionMutation = useCreateSubjectQuizSessionMutation();
  const submitAnswerMutation = useSubmitSubjectQuizAnswerMutation();

  return {
    quizSession: createSessionMutation.data,
    createSession: createSessionMutation.mutateAsync,
    isCreateSessionError: createSessionMutation.isError,
    isCreatingSession: createSessionMutation.isPending,
    submitAnswer: submitAnswerMutation.mutate,
    submitAnswerAsync: submitAnswerMutation.mutateAsync,
    isSubmitting: submitAnswerMutation.isPending,
  };
};

/** Consumer hook for subject quiz practice session (one question at a time with feedback) */
export const useSubjectPracticeQuiz = () => {
  const createSessionMutation = useCreateSubjectQuizPracticeSessionMutation();
  const submitAnswerMutation = useSubmitSubjectQuizPracticeAnswerMutation();

  return {
    quizSession: createSessionMutation.data,
    createSession: createSessionMutation.mutateAsync,
    isCreateSessionError: createSessionMutation.isError,
    isCreatingSession: createSessionMutation.isPending,
    submitAnswerAsync: submitAnswerMutation.mutateAsync,
    isSubmitting: submitAnswerMutation.isPending,
  };
};

/** Check if learner has completed level assessment for a given subject.
 *  Reuses cached study page data — no extra API call.
 *  Returns subjectExists=false when subjectId not found in subject list. */
export const useStudySubjectAccessCheck = (subjectId: string | undefined) => {
  const query = useStudyPageDataQuery();
  const subject = useMemo(() => query.data?.subjects.find((s) => s.id === subjectId) ?? null, [query.data, subjectId]);

  return {
    subject,
    hasLevelAssessment: subject?.hasLevelAssessment ?? false,
    isLoading: query.isLoading,
  };
};

/** Consumer hook for study vocabulary quiz session management */
export const useStudyVocabQuiz = () => {
  const createSessionMutation = useCreateVocabularyQuizSessionMutation();
  const submitAnswerMutation = useSubmitVocabularyQuizAnswerMutation();

  return {
    createSession: createSessionMutation.mutateAsync,
    isCreatingSession: createSessionMutation.isPending,
    submitAnswerAsync: submitAnswerMutation.mutateAsync,
    isSubmitting: submitAnswerMutation.isPending,
  };
};

/**
 * Hook for fetching boost/encouragement message.
 * Only fetches when enabled (i.e., isBoostVisible=true).
 */
export const useBoostMessage = (enabled: boolean) => {
  const query = useBoostMessageQuery(enabled);

  return {
    message: query.data ?? null,
    isLoading: query.isLoading,
  };
};
